<template>
  <CustomModal
    :title="isEditMode ? 'Update Dashboard' : 'Create Dashboard'"
    width="34vw"
    v-model:visible="internalVisible"
  >
    <div :id="`${source}_add_dashboard_section`" class="flex flex-col gap-6">
      <div id="name" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Name</span>
        <a-input
          ref="nameInputRef"
          v-model:value="formData.name"
          class="input_medium"
          placeholder="Enter name"
          @keyup.enter="handleSubmit"
          :autofocus="true"
        />
      </div>

      <div v-if="showTypeSelector" id="type" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Type</span>
        <CustomSelector
          v-model="formData.type"
          :source="`${source}_add_dashboard_section`"
          :options="analyticsEditorTypeOptions"
          :horizontal-align="customSelectorHorizontalAlignOptions.START"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row items-center justify-end gap-2 w-full">
        <a-button
          class="secondary_medium"
          :disabled="creating"
          @click="handleCancel"
        >
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          :disabled="isSubmitDisabled"
          :loading="creating"
          @click="handleSubmit"
        >
          {{
            creating
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update"
                : "Create"
          }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { useAnalyticsDashboard } from "@/src/composables/useAnalyticsDashboard.js";
import { customSelectorHorizontalAlignOptions } from "@/src/constants/customComponentConstants.js";
import {
  getAnalyticsEditorTypeOptions,
  analyticsEditorTypes,
  dashboardEvents,
  analyticsDefaultDashboardName,
} from "@/src/constants/analyticConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomSelector from "@/src/components/CustomSelector.vue";

const router = useRouter();
const { createDashboard } = useAnalyticsDashboard();

const analyticsEditorTypeOptions = computed(() => {
  return getAnalyticsEditorTypeOptions();
});

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
  space: { type: Object, default: null },
  dashboard: { type: Object, default: null },
  mode: { type: String, default: "create" },
});

const emit = defineEmits(["update:visible"]);

const creating = ref(false);
const internalVisible = ref(props.visible);
const nameInputRef = ref(null);

const formData = reactive({
  name: analyticsDefaultDashboardName,
  type: analyticsEditorTypes.MANUAL,
});

const isEditMode = computed(() => {
  return props.mode === "edit";
});

const isSubmitDisabled = computed(() => {
  return !formData.name || formData.name.trim() === "" || creating.value;
});

const showTypeSelector = computed(() => {
  return !isEditMode.value && !!localStorage.getItem("analytics_editor");
});

const resetForm = () => {
  try {
    Object.assign(formData, {
      name: analyticsDefaultDashboardName,
      type: analyticsEditorTypes.MANUAL,
    });
    creating.value = false;
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const handleCancel = () => {
  try {
    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while closing modal: ", error);
  }
};

const handleSubmit = async () => {
  if (isEditMode.value) {
    await toUpdateDashboard();
  } else {
    await toCreateDashboard();
  }
};

const toCreateDashboard = async () => {
  try {
    creating.value = true;

    if (formData.type === analyticsEditorTypes.MANUAL) {
      await router.push({
        name: "analytics-templates",
        query: {
          name: formData.name,
          type: formData.type,
          space_id: props.space?.id || null,
        },
      });

      internalVisible.value = false;
      resetForm();
    } else {
      const result = await createDashboard(
        {
          name: formData.name,
          type: formData.type,
          space_id: props.space?.id || null,
        },
        router,
      );

      if (result) {
        internalVisible.value = false;
        resetForm();
      }
    }
  } catch (error) {
    console.error("An error occurred while creating dashboard: ", error);
    message.error("An error occurred while creating a dashboard!");
  } finally {
    creating.value = false;
  }
};

const toUpdateDashboard = async () => {
  try {
    creating.value = true;

    const payload = {
      name: formData.name,
    };

    const response = await AnalyticService.V3.updateDashboard(
      props.dashboard?.id,
      payload,
    );
    if (response?.success) {
      message.success(`Dashboard renamed to ${formData.name} successfully!`);

      internalVisible.value = false;
      resetForm();

      eventBus.emit("dashboard-manipulated", dashboardEvents.UPDATED);
    }
  } catch (error) {
    console.error("An error occurred while updating dashboard: ", error);
    message.error("An error occurred while updating dashboard!");
  } finally {
    creating.value = false;
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      await nextTick();
      nameInputRef.value?.focus();
    }
  },
  { immediate: true },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
    if (!newVal) {
      resetForm();
    }
  },
);

onMounted(() => {
  if (isEditMode.value && props.dashboard) {
    formData.name = props.dashboard.name || "";
  }
});

watch(
  () => [props.dashboard, props.visible, props.mode],
  ([dashboard, visible, mode]) => {
    if (visible && mode === "edit" && dashboard) {
      formData.name = dashboard.name || "";
    }
  },
  { immediate: true },
);
</script>
